// src/utils/storage.js - Fixed version
// This provides fallback mechanisms for storage access

/**
 * Gets data from localStorage with fallback mechanisms
 * @param {string} key - The storage key to retrieve
 * @returns {Promise<any>} The parsed data or null
 */
export async function getFileStorage(key) {
  try {
    // First try the localStorage approach
    const data = localStorage.getItem(key);
    if (data) {
      return JSON.parse(data);
    }
    
    // If localStorage doesn't have it, return empty result
    console.log(`No data found for key: ${key}`);
    return null;
  } catch (error) {
    console.error(`Error reading from storage for key ${key}:`, error);
    return null;
  }
}

/**
 * Sets data to localStorage with error handling
 * @param {string} key - The storage key
 * @param {any} data - Data to store (will be JSON stringified)
 * @returns {Promise<boolean>} Success indicator
 */
export async function setFileStorage(key, data) {
  try {
    // Stringify the data with proper error handling
    const jsonData = JSON.stringify(data, (k, v) => {
      // Handle circular references or invalid data
      if (typeof v === 'function') return undefined;
      if (k === 'initPromise') return undefined; // Skip promise objects
      return v;
    });
    
    // Set to localStorage
    localStorage.setItem(key, jsonData);
    return true;
  } catch (error) {
    console.error(`Error saving to storage for key ${key}:`, error);
    return false;
  }
}

/**
 * Clears a specific key from storage
 * @param {string} key - The key to clear
 * @returns {Promise<boolean>} Success indicator
 */
export async function clearFileStorage(key) {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error clearing storage for key ${key}:`, error);
    return false;
  }
}

/**
 * Gets all available keys in storage
 * @returns {Promise<string[]>} List of available keys
 */
export async function getStorageKeys() {
  try {
    return Object.keys(localStorage);
  } catch (error) {
    console.error('Error getting storage keys:', error);
    return [];
  }
}